import React, { useState, useEffect } from 'react'
import { useTranslation } from '@structura/i18n'

interface ReportSettingsDialogProps {
  show: boolean
  onClose: () => void
  onGenerate: (settings: ReportSettings) => Promise<void>
  generating: boolean
  getSessionHeaders: () => HeadersInit
}

export interface ReportSettings {
  projectName: string
  projectNumber: string
  engineerName: string
  companyName: string
  projectDescription: string
  projectPurpose: string
}

const ReportSettingsDialog: React.FC<ReportSettingsDialogProps> = ({ show, onClose, onGenerate, generating, getSessionHeaders }) => {
  const { t } = useTranslation()
  const [projectName, setProjectName] = useState('Beam Design')
  const [projectNumber, setProjectNumber] = useState('')
  const [engineerName, setEngineerName] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [projectDescription, setProjectDescription] = useState('')
  const [projectPurpose, setProjectPurpose] = useState('')
  const [nextProjectNumber, setNextProjectNumber] = useState(1)

  const loadNextProjectNumber = async () => {
    try {
      const response = await fetch('/api/CalculationReport/list', {
        headers: getSessionHeaders()
      })

      if (response.ok) {
        const data = await response.json()
        // The endpoint returns a bare array (same contract as ReportTab); `data.items` was never
        // populated and silently reset numbering to 1.
        const items = Array.isArray(data) ? data : (data.items || [])

        // Count "Beam Design" projects
        const beamDesignCount = items.filter((item: any) => 
          item.projectName && item.projectName.startsWith('Beam Design')
        ).length
        
        const nextNumber = beamDesignCount + 1
        setNextProjectNumber(nextNumber)
        setProjectNumber(nextNumber.toString())
      }
    } catch (err) {
      console.error('Failed to load report list:', err)
      setProjectNumber('1')
    }
  }

  // Load report list when dialog opens to calculate next project number
  /* eslint-disable react-hooks/set-state-in-effect -- async fetch sets state after await */
  useEffect(() => {
    if (show) {
      loadNextProjectNumber()
    }
  }, [show]) // eslint-disable-line react-hooks/exhaustive-deps
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleGenerate = async () => {
    const settings: ReportSettings = {
      projectName: projectName.trim() || 'Beam Design',
      projectNumber: projectNumber.trim() || nextProjectNumber.toString(),
      engineerName: engineerName.trim(),
      companyName: companyName.trim(),
      projectDescription: projectDescription.trim(),
      projectPurpose: projectPurpose.trim()
    }
    await onGenerate(settings)
  }

  if (!show) {
    return null
  }

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
      <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxWidth: '900px', margin: '1.75rem auto' }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{t('beam.report.settingsTitle')}</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={generating}></button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label htmlFor="projectName" className="form-label">
                {t('beam.report.projectName')}
              </label>
              <input
                type="text"
                className="form-control"
                id="projectName"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder={t('beam.report.projectNamePlaceholder')}
                disabled={generating}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="projectNumber" className="form-label">
                {t('beam.report.projectId')}
              </label>
              <input
                type="text"
                className="form-control"
                id="projectNumber"
                value={projectNumber}
                onChange={(e) => setProjectNumber(e.target.value)}
                placeholder={t('beam.report.autoGenerated')}
                disabled={generating}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="engineerName" className="form-label">{t('beam.report.designerName')}</label>
              <input
                type="text"
                className="form-control"
                id="engineerName"
                value={engineerName}
                onChange={(e) => setEngineerName(e.target.value)}
                placeholder={t('beam.report.designerPlaceholder')}
                disabled={generating}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="companyName" className="form-label">{t('beam.report.companyName')}</label>
              <input
                type="text"
                className="form-control"
                id="companyName"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder={t('beam.report.companyPlaceholder')}
                disabled={generating}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="projectDescription" className="form-label">{t('beam.report.projectDescription')}</label>
              <textarea
                className="form-control"
                id="projectDescription"
                rows={3}
                value={projectDescription}
                onChange={(e) => setProjectDescription(e.target.value)}
                placeholder={t('beam.report.descriptionPlaceholder')}
                disabled={generating}
              />
              <small className="text-muted">{t('beam.report.appearsSection1')}</small>
            </div>
            <div className="mb-3">
              <label htmlFor="projectPurpose" className="form-label">{t('beam.report.purpose')}</label>
              <textarea
                className="form-control"
                id="projectPurpose"
                rows={3}
                value={projectPurpose}
                onChange={(e) => setProjectPurpose(e.target.value)}
                placeholder={t('beam.report.purposePlaceholder')}
                disabled={generating}
              />
              <small className="text-muted">{t('beam.report.appearsSection1')}</small>
            </div>
            <div className="beam-alert-info mb-0">
              <small>
                <strong>{t('beam.report.noteLabel')}</strong> {t('beam.report.noteText')}
              </small>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={generating}>
              {t('beam.loads.cancel')}
            </button>
            <button type="button" className="btn beam-btn-primary" onClick={handleGenerate} disabled={generating}>
              {generating ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {t('beam.report.generating')}
                </>
              ) : (
                t('beam.report.generateReport')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ReportSettingsDialog

